import {
  emptyBasicUseDocument,
  ThreadSearchResult,
  ThreadSelection,
  ThreadType,
  USER_ROLES,
  UserRoleItem,
} from '@hb/shared'
import React, { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react'

import { useApp, useFetchData } from '../../contexts'
import { useMySettings } from '../../hooks/user/useMySettings'
import { useAuth } from '../../store/auth'
import { emptyMessagesWidget } from '../constants'
import { useMyThreads } from '../hooks/useMyThreads'
import { MessagingWidget } from '../types/MessagingWidget'
import { ThreadsListThread } from '../types/ThreadsList'
import { ThreadsListContextData } from '../types/ThreadsListContextData'

export const ThreadsListContext = createContext<ThreadsListContextData>({
  uid: null,
  threads: {
    data: [],
    selectedTabThreads: [],
    deepSearchLoading: false,
    deepSearchResults: null,
    deepSearchThreads: () => {},
    numUnreadThreads: 0,
    loading: false,
    error: null,
  },
  numUnread: 0,
  notificationType: null,
  closeThread: () => {},
  isOpen: false,
  onOpen: () => {},
  onClose: () => {},
  threadsView: 'patient',
  widget: emptyMessagesWidget,
  settings: emptyBasicUseDocument,
  selectedThread: {
    thread: null,
    // messages: {
    //   data: [],
    //   fetchMoreData: () => Promise.resolve({ data: [], error: null }),
    //   hasMoreData: false,
    //   loading: false,
    //   error: null,
    // },
  },
  goToThread: () => {},
})

const useSelectedThread = (
  threadSelection: ThreadSelection | null,
  threads: ThreadsListThread[],
) => {
  // const { id: threadId } = threadSelection ?? {}
  const thread = useMemo(
    () => (threadSelection ? (threads.find(t => t.threadId === threadSelection.id) ?? null) : null),
    [threads, threadSelection],
  )

  const [searchQuery, setSearchQuery] = useState('')

  return useMemo(
    () => ({
      thread,
      searchQuery,
      updateSearchQuery: setSearchQuery,
    }),
    [thread, searchQuery],
  )
}

export interface MyThreadsListProviderProps {
  playNotificationSound: (() => void) | null
  getDocumentIdPath: () => any
  onGoToThread?: (thread: ThreadSelection) => void
  onCloseThread?: () => void
  widget: MessagingWidget
  uid: string | null
  forcePatientThreadsView?: boolean
}

export const MyThreadsListProvider = ({
  children,
  playNotificationSound,
  getDocumentIdPath,
  onGoToThread,
  onCloseThread,
  widget,
  uid,
  forcePatientThreadsView = false,
}: PropsWithChildren<MyThreadsListProviderProps>) => {
  const [isOpen, setIsOpen] = useState(false)
  const onClose = useCallback(() => setIsOpen(false), [])
  const onOpen = useCallback(() => setIsOpen(true), [])
  const { setSearchResult, updateSearchQuery, setThreadSelection, threadSelection } = widget ?? {}
  const { useDocument } = useFetchData()
  const { appName } = useApp()

  const { accessLevel, user } = useAuth()

  const threadsView = useMemo(
    () =>
      accessLevel === 'patient' ||
      (accessLevel === 'practice' && appName === 'app') ||
      forcePatientThreadsView
        ? 'patient'
        : 'admin',
    [accessLevel, appName, forcePatientThreadsView],
  )
  const { data: userRoleItem } = useDocument<UserRoleItem>(USER_ROLES, uid)
  const { webPushNotifications } = userRoleItem ?? {}
  const { messaging: notificationType = null } = webPushNotifications ?? {}
  const settings = useMySettings(uid)
  const threads = useMyThreads(uid, widget, playNotificationSound, threadsView, getDocumentIdPath)
  // const { thread: threadSelection } = widgetData ?? {}
  const { data: threadsData } = threads ?? {}

  // const [threadSelection, setThreadSelection] = useState<ThreadSelection | null>(null)
  const usedThreadSelection = useMemo<ThreadSelection | null>(() => {
    if (!uid) return null
    if (threadsView === 'patient')
      return {
        accessLevel: 'patient',
        id: uid,
        type: ThreadType.PATIENT,
        title: 'Chat with Hamilton Billing',
      }
    return threadSelection
  }, [threadSelection, threadsView, uid])

  const selectedThread = useSelectedThread(usedThreadSelection, threadsData)
  const goToThread = useCallback(
    ({
      id,
      title,
      type,
      accessLevel,
      searchResult: goToSearchResult,
    }: ThreadSelection & {
      searchResult?: ThreadSearchResult
    }) => {
      updateSearchQuery('')
      if (onGoToThread) {
        onGoToThread({
          id,
          type,
          title: title ?? '',
          accessLevel,
        })
      }
      setThreadSelection({
        id,
        type,
        title: title ?? '',
        accessLevel,
      })
      if (goToSearchResult) setSearchResult({ ...goToSearchResult, selectedOn: Date.now() })
    },
    [setSearchResult, updateSearchQuery, onGoToThread, setThreadSelection],
  )

  const numUnread = useMemo(() => {
    if (threadsView === 'patient') return user?.unreadThreadMessages ?? 0
    return threads.numUnreadThreads
  }, [threads.numUnreadThreads, threadsView, user])

  const closeThread = useCallback(() => {
    setThreadSelection(null)
    if (onCloseThread) onCloseThread()
  }, [onCloseThread, setThreadSelection])

  const contextData = useMemo<ThreadsListContextData>(
    () => ({
      threads,
      uid,
      widget,
      numUnread,
      selectedThread,
      goToThread,
      closeThread,
      notificationType,
      settings,
      isOpen,
      onClose,
      onOpen,
      threadsView,
    }),
    [
      threads,
      uid,
      numUnread,
      widget,
      selectedThread,
      goToThread,
      closeThread,
      notificationType,
      settings,
      isOpen,
      onClose,
      onOpen,
      threadsView,
    ],
  )

  return <ThreadsListContext.Provider value={contextData}>{children}</ThreadsListContext.Provider>
}
